// src/pages/investmentFunds/dashboard/Deposits.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { push } from "connected-react-router";

import { selectors as investmentfundsSelectors } from "reducers/investmentfunds";
import Container from "pages/_components/Container";
import CustomText from "pages/_components/CustomText/CustomText";
import Button from "pages/_components/Button";
import * as i18n from "util/i18n";
import "../../../styles/components/modules/_DashboardCard.scss";

const Deposits = ({ currency }) => {
    const dispatch = useDispatch();
    const allDeposits = useSelector((state) => investmentfundsSelectors.getDeposits(state) || []);

    const deposits = useMemo(() => allDeposits.filter((d) => d.currency === currency), [allDeposits, currency]);

    const isEmpty = deposits.length === 0;

    const totalAmount = useMemo(() => deposits.reduce((sum, d) => sum + (d.amountTransfered || 0), 0), [deposits]);

    const formatAmount = (amount) =>
        amount.toLocaleString("es-AR", { style: "currency", currency, minimumFractionDigits: 2 });

    const handleShowMore = () => {
        dispatch(push("/deposits"));
    };

    // Máx. 2 columnas x 4 ítems = 8 visibles (keys estables)
    const visibleColumns = useMemo(() => {
        const maxVisible = 8;
        const perCol = 4;
        const take = deposits.slice(0, maxVisible);

        const cols = [];
        for (let i = 0; i < take.length && cols.length < 2; i += perCol) {
            const slice = take.slice(i, i + perCol);
            const key = slice.map((d) => d.id).join("_") || `col_${i}`; // clave estable por ids
            cols.push({ key, items: slice });
        }
        return cols;
    }, [deposits]);

    return (
        <div className="above-the-fold">
            <Container className="container--layout flex-grow">
                <div className={`card card--deposits ${isEmpty ? "card--disabled" : ""}`}>
                    <div className="card__header">
                        <CustomText
                            text="investmentFunds.dashboard.deposits"
                            textType="I18N"
                            className="card__header-title"
                        />
                        {!isEmpty && (
                            <Button
                                bsStyle="link"
                                className="card__header-action"
                                onClick={handleShowMore}
                                disabled={isEmpty}>
                                <CustomText text="global.showMore" textType="I18N" />
                                <span className="card__header-icon">→</span>
                            </Button>
                        )}
                    </div>

                    {isEmpty ? (
                        <div className="card__empty-message">
                            <CustomText
                                text={i18n.get(
                                    "investmentFunds.dashboard.noInvest.Deposits",
                                    "Actualmente, no posees Plazos Fijos",
                                )}
                                textType="plain"
                                className="card__empty-text"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="card__columns">
                                {visibleColumns.map((col) => (
                                    <div className="card__column" key={col.key}>
                                        <ul className="card__list" aria-label="Plazos fijos">
                                            {col.items.map((d) => (
                                                <li key={d.id} className="card__list-item">
                                                    <span className="card__bullet card__bullet--deposits" />
                                                    <CustomText
                                                        text={d.depositNumber}
                                                        textType="plain"
                                                        className="card__text"
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            <div className="card__total">
                                <CustomText
                                    text="investmentFunds.dashboard.deposits.totalAmount"
                                    textType="I18N"
                                    className="card__total-label"
                                />
                                <CustomText
                                    text={formatAmount(totalAmount)}
                                    textType="plain"
                                    className="card__total-value"
                                />
                            </div>
                        </>
                    )}
                </div>
            </Container>
        </div>
    );
};

Deposits.propTypes = {
    currency: PropTypes.oneOf(["ARS", "USD"]).isRequired,
};

export default Deposits;
